import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom/cjs/react-router-dom";
import Header from "../../header/header";
import NavBar from "../../nav/nav-bar";
import Footer from "../../footer/footer";
import detail from "../../assets/images/detail.svg";
import page from "../../assets/images/page.svg";
import callReport from "../../assets/images/call-report.svg";
import score from "../../assets/images/score.svg";
import Service from "../../webservice/http";
import ClipLoader from "react-spinners/ClipLoader";
import { IoArrowBack } from "react-icons/io5";
import { useHistory } from "react-router-dom";

import { NotificationContainer } from "react-notifications";
import { storage } from "../../utils/storage";

const getVersionColor = (version) => {
  if (!version || version === "Original") return "";
  const versionNum =
    typeof version === "number"
      ? version
      : parseInt(version.match(/\d+/)?.[0] || 0);

  // Colors strategically chosen for maximum visual distinction
  // Alternating between warm/cool and different color families
  const fixedColors = [
    "hsla(210, 90%, 88%, 0.8)", // Bright Blue
    "hsla(15, 85%, 88%, 0.8)", // Coral
    "hsla(280, 75%, 90%, 0.8)", // Purple
    "hsla(55, 95%, 82%, 0.8)", // Bright Yellow
    "hsla(340, 80%, 92%, 0.8)", // Rose Pink
    "hsla(175, 65%, 88%, 0.8)", // Teal
    "hsla(35, 90%, 85%, 0.8)", // Orange
    "hsla(250, 70%, 89%, 0.8)", // Lavender
    "hsla(155, 55%, 87%, 0.8)", // Mint Green
    "hsla(5, 80%, 90%, 0.8)", // Light Red
    "hsla(195, 85%, 87%, 0.8)", // Sky Blue
    "hsla(300, 75%, 91%, 0.8)", // Magenta
    "hsla(75, 70%, 86%, 0.8)", // Lime
    "hsla(230, 80%, 89%, 0.8)", // Royal Blue
    "hsla(120, 50%, 88%, 0.8)", // Soft Green
  ];

  if (versionNum >= 1 && versionNum <= 15) {
    return fixedColors[versionNum - 1];
  }

  // For versions > 15, use deterministic generation with more variation
  // Spread across the spectrum with different saturation/lightness
  const baseHue = (versionNum * 137.5) % 360;

  // Avoid muddy greens (100-140) which can look too similar
  let hue = baseHue;
  if (hue > 100 && hue < 140) {
    hue = (hue + 80) % 360;
  }

  // Vary saturation and lightness based on version number to add more distinction
  const saturation = 65 + ((versionNum * 13) % 25); // 65-90%
  const lightness = 86 + ((versionNum * 7) % 8); // 86-94%

  return `hsla(${Math.round(hue)}, ${saturation}%, ${lightness}%, 0.8)`;
};

function Detail() {
  const history = useHistory();
  const { id } = useParams();
  const service = useMemo(() => new Service(), []);
  const [data, setData] = useState({});
  const [Loading, setLoading] = useState(false);

  const [showAllInfo, setShowAllInfo] = useState(false);
  const [fullApiResponse, setFullApiResponse] = useState(null);

  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [changedFieldsList, setChangedFieldsList] = useState([]);
  const [baselineData, setBaselineData] = useState(null);
  const [allVersions, setAllVersions] = useState([]);

  // Notes state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesInput, setNotesInput] = useState("");
  const [notesData, setNotesData] = useState({ notes: [] });
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const processAllVersions = (apiResponse) => {
    if (!apiResponse) return [];

    const originalData = { ...apiResponse };
    const originalVersion = {
      label: "Original",
      data: originalData,
      changes: [],
      editedBy: apiResponse.edited_by || "System",
      editedAt: apiResponse.created_at || apiResponse.call_date || "-",
      isOriginal: true,
      versionNum: 0,
      color: "#271078",
    };

    const versions = [originalVersion];

    if (
      apiResponse.changes &&
      Array.isArray(apiResponse.changes) &&
      apiResponse.changes.length > 0
    ) {
      const sortedChanges = [...apiResponse.changes].sort(
        (a, b) => a.version_number - b.version_number,
      );

      let currentData = { ...originalData };

      sortedChanges.forEach((versionChange) => {
        const nextData = { ...currentData };

        if (versionChange.changes && Array.isArray(versionChange.changes)) {
          versionChange.changes.forEach((c) => {
            if (c.new_value === "True" || c.new_value === true)
              nextData[c.field_name] = true;
            else if (c.new_value === "False" || c.new_value === false)
              nextData[c.field_name] = false;
            else nextData[c.field_name] = c.new_value;
          });
        }

        if (versionChange.reviewer_percentage !== undefined) {
          nextData.reviewer_percentage = versionChange.reviewer_percentage;
        }

        const vColor = getVersionColor(versionChange.version_number);

        versions.push({
          label:
            versionChange.version_number === 1
              ? "Report 1.0"
              : `Revised Report ${versionChange.version_number}.0`,
          data: nextData,
          changes: versionChange.changes || [],
          editedBy: versionChange.edited_by || "N/A",
          editedAt: versionChange.edited_at || "-",
          color: vColor,
          versionNum: versionChange.version_number,
          isOriginal: false,
          revised_points: versionChange.revised_points,
        });

        currentData = nextData;
      });
    }

    return versions;
  };

  const isFieldChangedInVersion = (fieldName, version) => {
    if (version.isOriginal) return false;
    return version.changes?.some((c) => c.field_name === fieldName);
  };

  const getFieldTrail = (fieldName) => {
    const trail = [];
    allVersions.forEach((v) => {
      if (v.isOriginal) return;
      const change = v.changes.find((c) => c.field_name === fieldName);
      if (change) {
        trail.push({
          label: v.label,
          from: change.old_value,
          to: change.new_value,
          color: v.color,
        });
      }
    });
    return trail;
  };

  const hasProcessedLocationState = React.useRef(false);

  useEffect(() => {
    if (location.state?.isEdit && !hasProcessedLocationState.current) {
      setIsEditMode(true);
      setShowAllInfo(true);
      hasProcessedLocationState.current = true;
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchDetail = React.useCallback(() => {
    setLoading(true);
    service
      .get(`api/call_verification/collection-strategy/${id}/`)
      .then((apiResponse) => {
        // The API wraps the record under a "data" key
        const response = apiResponse?.data ?? apiResponse;
        setFullApiResponse(response);
        const processedVersions = processAllVersions(response);
        setAllVersions(processedVersions);

        if (response?.changes && response.changes.length > 0) {
          const sortedChanges = [...response.changes].sort(
            (a, b) => b.version_number - a.version_number,
          );
          const latestChange = sortedChanges[0];
          // const versionLabel =
          //   latestChange.version_number === 1
          //     ? "Report 1.0"
          //     : `Revised Report ${latestChange.version_number}.0`;

          // setSelectedVersion(versionLabel);

          const latestData = { ...(response || {}) };

          const sortedAllChanges = [...response.changes].sort(
            (a, b) => a.version_number - b.version_number,
          );

          sortedAllChanges.forEach((v) => {
            if (v.version_number <= latestChange.version_number) {
              if (v.changes && v.changes.length > 0) {
                v.changes.forEach((c) => {
                  if (c.new_value === "True" || c.new_value === true) {
                    latestData[c.field_name] = true;
                  } else if (c.new_value === "False" || c.new_value === false) {
                    latestData[c.field_name] = false;
                  } else {
                    latestData[c.field_name] = c.new_value;
                  }
                });
              }

              if (v.version_number === latestChange.version_number) {
                latestData.reviewer_percentage = v.reviewer_percentage;
              }
            }
          });

          setData(latestData);
          setBaselineData(latestData);

          if (!isEditMode) {
          }
        } else {
          setData(response || {});
          setBaselineData(response || {});
          // setSelectedVersion("Original");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch detail:", err);
        setLoading(false);
      });
  }, [id, service, isEditMode]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);
  // console.log(allVersions);

  const handleCloseModal = () => {
    setShowAllInfo(false);
    setIsEditMode(false);
    setShowConfirmSave(false);
    hasProcessedLocationState.current = false;
  };

  const handleFieldUpdate = (key, value) => {
    const updatedData = { ...data };
    updatedData[key] = value;
    setData(updatedData);
  };

  const handleSaveAttempt = () => {
    const changes = [];
    parameters.forEach((category) => {
      category.items.forEach((item) => {
        const originalVal = !!baselineData?.[item.valueKey];
        const currentVal = !!data[item.valueKey];
        if (originalVal !== currentVal) {
          changes.push({
            label: item.label,
            from: originalVal ? "Yes" : "No",
            to: currentVal ? "Yes" : "No",
          });
        }
      });
    });

    if (changes.length === 0) {
      handleCloseModal();
      return;
    }

    setChangedFieldsList(changes);
    setShowConfirmSave(true);
  };

  const executeSave = () => {
    const payload = {};
    parameters.forEach((category) => {
      category.items.forEach((item) => {
        payload[item.valueKey] = !!data[item.valueKey];
      });
    });

    service
      .post(`api/call_verification/edit-collection-strategy/${id}/`, payload)
      .then(() => {
        setIsEditMode(false);
        setShowConfirmSave(false);
        setShowAllInfo(false);
        hasProcessedLocationState.current = false;
        fetchDetail();
      })
      .catch((err) => {
        console.error("Save failed:", err);
      });
  };

  // ── Notes helpers ──────────────────────────────────────
  const fetchNotes = React.useCallback(() => {
    if (!id) return;
    service
      .get(`api/call_verification/collection-strategy/${id}/`)
      .then((res) => {
        const response = res?.data ?? res;
        const comment = response?.collection_strategy_review_comment;
        if (comment) {
          setNotesData({
            notes: [{ id: "review", text: comment, date: "" }],
          });
        } else {
          setNotesData({ notes: [] });
        }
      })
      .catch((err) => {
        console.error(
          "Failed to fetch collection strategy review comment:",
          err
        );
      });
  }, [id, service]);

  const handleAddNote = () => {
    const trimmed = notesInput.trim();
    if (!trimmed) return;

    service
      .post(`api/call_verification/collection-strategy/${id}/`, {
        collection_strategy_review_comment: trimmed,
      })
      .then(() => {
        setNotesInput("");
        setShowAddNoteForm(false);
        setShowNotesModal(false);
        fetchNotes();
      })
      .catch((err) =>
        console.error(
          "Failed to post collection strategy review comment:",
          err
        )
      );
  };

  const confirmDeleteNote = () => {
    setNoteToDelete(null);
  };

  useEffect(() => {
    if (id) fetchNotes();
  }, [id, fetchNotes]);

  const parameters = [
    {
      title: "Probing Questions Effectiveness",
      totalPoints: 10,
      items: [
        {
          label:
            "Reason identification for RFD, refusal to pay, broken promise, capacity to pay and source of funds.",
          valueKey: "probing_customer_rfd_reason",
          pointsKey: "probing_customer_rfd_reason_points",
          pointsAvailable: 4,
        },
        {
          label:
            "Challenges uncertain statements before accepting payment arrangements.",
          valueKey: "probing_customer_challenge_statement",
          pointsKey: "probing_customer_challenge_statement_points",
          pointsAvailable: 3,
        },
        {
          label: "Asked alternate number (Customer).",
          valueKey: "probing_customer_alternative_number",
          pointsKey: "probing_customer_alternative_number_points",
          pointsAvailable: 3,
        },
        {
          label: "Asked alternate number (Related party).",
          valueKey: "probing_non_customer_alternative_number",
          pointsKey: "probing_non_customer_alternative_number_points",
          pointsAvailable: 5,
        },

        {
          label: "Wrong-number follow-up questions (Related party).",
          valueKey: "probing_non_customer_wrong_number",
          pointsKey: "probing_non_customer_wrong_number_points",
          pointsAvailable: 5,
        },
      ],
      pointgot: "collection_probing_total_points",
    },
    {
      title: "Payment Resolution Actions",
      totalPoints: 20,
      items: [
        {
          label:
            "Use of risk and levers (provide risk to persuade the client, consequence of non-payment).",
          valueKey: "payment_customer_risk_levers",
          pointsKey: "payment_customer_risk_levers_points",
          pointsAvailable: 4,
        },
        {
          label: "Best-fit payment solution.",
          valueKey: "payment_customer_best_solution",
          pointsKey: "payment_customer_best_solution_points",
          pointsAvailable: 4,
        },
        {
          label:
            "Follows standard guidelines when offering or accepting partial payments.",
          valueKey: "payment_customer_partial_payment",
          pointsKey: "payment_customer_partial_payment_points",
          pointsAvailable: 3,
        },
        {
          label: "Addresses account holder's concern regarding payment issues.",
          valueKey: "payment_customer_address_concern",
          pointsKey: "payment_customer_address_concern_points",
          pointsAvailable: 3,
        },
        {
          label: "Offers the correct due date.",
          valueKey: "payment_customer_correct_due_date",
          pointsKey: "payment_customer_correct_due_date_points",
          pointsAvailable: 3,
        },
        {
          label: "Delivers complete RTP closing spiel.",
          valueKey: "payment_customer_rtp_closing",
          pointsKey: "payment_customer_rtp_closing_points",
          pointsAvailable: 3,
        },
        {
          label:
            "Advises the recipient to relay message to the customer (Related party).",
          valueKey: "payment_non_customer_relay_message",
          pointsKey: "payment_non_customer_relay_message_points",
          pointsAvailable: 20,
        },
      ],
      pointgot: "collection_payment_resolution_actions_total_points",
    },
    {
      title: "Payment Delay Consequences",
      totalPoints: 10,
      items: [
        {
          label:
            "Educates the customer about the consequences or benefits of not paying or paying the loan.",
          valueKey: "payment_delay_educates_consequences",
          pointsKey: "payment_delay_educates_consequences_points",
          pointsAvailable: 4,
        },
        {
          label: "Offer Capacity-Fit Solution.",
          valueKey: "payment_delay_offers_solution",
          pointsKey: "payment_delay_offers_solution_points",
          pointsAvailable: 3,
        },
        {
          label: "Advise Account Status & Negative Impact.",
          valueKey: "payment_delay_advises_status",
          pointsKey: "payment_delay_advises_status_points",
          pointsAvailable: 3,
        },
      ],
      pointgot: "collection_payment_delay_consequences_total_points",
    },
  ];

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />
      <NotificationContainer />
      {Loading ? (
        <div
          className="flex justify-center items-center"
          style={{ height: "100vh" }}
        >
          <ClipLoader color="#2056FF" size="50px" />
        </div>
      ) : (
        <div>
          <div className="container-fluid">
            <div
              className="my-call flex flex-col gap-4"
              style={{ background: "none" }}
            >
              <div className="flex items-center bg-white px-4 py-10 rounded-xl gap-6 justify-between">
                <div className="flex items-center gap-4">
                  <img src={detail} alt="detail" />
                  <span
                    className="text-[#484848] font-bold"
                    style={{ color: "#484848", fontSize: "18px" }}
                  >
                    Detailed Report <br />
                    Call Reference: ( Ref# :{fullApiResponse?.call_reference})
                  </span>
                </div>
                <button
                  className={` flex items-center gap-4 mr-4 btn text-white hover:text-white hover:shadow-lg`}
                  style={{
                    // border: "1px solid gray",
                    backgroundColor: "#5BC0DE",
                  }}
                  onClick={() =>
                    history.push(`/call/${data?.call_id}/call-insight`)
                  }
                >
                  <IoArrowBack />
                  Call Insight
                </button>
              </div>

              <div className="flex items-center justify-between bg-white px-4 py-10 rounded-xl">
                <span className=" flex gap-4">
                  <img src={page} alt="detail" />
                  <span
                    className="font-bold"
                    style={{ color: "#271078", fontSize: "16px" }}
                  >
                    Details
                  </span>
                </span>
                {/* Notes Button – right side of the Details header */}
                {/* <button
                  onClick={() => {
                    document.getElementById('comments-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  style={{
                    backgroundColor: "#fff",
                    color: "#271078",
                    padding: "0.5rem 1.5rem",
                    borderRadius: "10px",
                    fontWeight: "700",
                    border: "2px solid #271078",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    boxShadow: "0 4px 6px -1px rgba(39, 16, 120, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#271078";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.color = "#271078";
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  Comments
                  {(notesData?.notes?.length ?? 0) > 0 && (
                    <span
                      style={{
                        backgroundColor: "#271078",
                        color: "white",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: "700",
                      }}
                    >
                      {notesData.notes.length}
                    </span>
                  )}
                </button> */}
              </div>

              <div
                className="h-auto bg-white px-8 py-16 rounded-xl flex flex-wrap gap-8"
                style={{
                  border: "1px solid #DFDFDF",
                  color: "#171717",
                  fontSize: "16px",
                }}
              >
                <div
                  className="px-6 py-4 flex  items-center  rounded-lg no whitespace-nowrap"
                  style={{
                    border: "1px solid #C2C2C2",
                    width: "398px",
                    height: "54px",
                    background: "white",
                    fontSize: "17px",
                    color: "#171717",
                  }}
                >
                  <span className=" w-1/2"> Call Date</span>
                  <span
                    className=" mx-8 h-10"
                    style={{ backgroundColor: "#828282", width: "1px" }}
                  ></span>
                  <span className="font-bold"> {data?.call_date || "-"}</span>
                </div>

                <div
                  className="px-6 py-4 flex  items-center  rounded-lg no whitespace-nowrap"
                  style={{
                    border: "1px solid #C2C2C2",
                    width: "398px",
                    height: "54px",
                    background: "white",
                    fontSize: "17px",
                    color: "#171717",
                  }}
                >
                  <span className=" w-1/2">Agent</span>
                  <span
                    className=" mx-8 h-10"
                    style={{ backgroundColor: "#828282", width: "1px" }}
                  ></span>
                  <span className="font-bold">{data?.agent || "-"}</span>
                </div>

                <div
                  className="px-6 py-4 flex  items-center  rounded-lg no whitespace-nowrap"
                  style={{
                    border: "1px solid #C2C2C2",
                    width: "398px",
                    height: "54px",
                    background: "white",
                    fontSize: "17px",
                    color: "#171717",
                  }}
                >
                  <span className=" w-1/2">Subscriber Name</span>
                  <span
                    className=" mx-8 h-10"
                    style={{ backgroundColor: "#828282", width: "1px" }}
                  ></span>
                  <span className="font-bold">
                    {data?.customer?.split(" ").slice(0, 2).join(" ") || "-"}
                  </span>
                </div>

                <div
                  className="px-6 py-4 flex  items-center  rounded-lg no whitespace-nowrap"
                  style={{
                    border: "1px solid #C2C2C2",
                    width: "398px",
                    height: "54px",
                    background: "white",
                    fontSize: "17px",
                    color: "#171717",
                  }}
                >
                  <span className=" w-1/2">Team Lead</span>
                  <span
                    className=" mx-8 h-10"
                    style={{ backgroundColor: "#828282", width: "1px" }}
                  ></span>
                  <span className="font-bold">{data?.team_lead || "-"}</span>
                </div>

                <div
                  className="px-6 py-4 flex  items-center  rounded-lg no whitespace-nowrap"
                  style={{
                    border: "1px solid #C2C2C2",
                    width: "398px",
                    height: "54px",
                    background: "white",
                    fontSize: "17px",
                    color: "#171717",
                  }}
                >
                  <span className=" w-1/2">Language</span>
                  <span
                    className=" mx-8 h-10"
                    style={{ backgroundColor: "#828282", width: "1px" }}
                  ></span>

                  <span className="font-bold">{data?.language || "-"}</span>
                </div>

                <div
                  className="px-6 py-4 flex  items-center  rounded-lg no whitespace-nowrap"
                  style={{
                    border: "1px solid #C2C2C2",
                    width: "398px",
                    height: "54px",
                    background: "white",
                    fontSize: "17px",
                    color: "#171717",
                  }}
                >
                  <span className=" w-1/2">Loan Product</span>
                  <span
                    className="mx-8 h-10"
                    style={{ backgroundColor: "#828282", width: "1px" }}
                  ></span>
                  <span className="font-bold">{data?.product_name || "-"}</span>
                </div>
              </div>

              {/* <div
                className="flex items-center justify-between bg-white px-4 py-8 rounded-xl "
                style={{ height: "74px" }}
              >
                <div className="flex justify-between items-center">
                  <span className="flex gap-4">
                    <img src={callReport} alt="detail" />
                    <span
                      className="font-bold"
                      style={{ color: "#271078", fontSize: "16px" }}
                    >
                      Collection Strategy
                    </span>
                  </span>
                </div>
                <span className=" flex items-center mr-14 gap-2">
                  <img src={score} alt="score" />
                  <span
                    className=" font-bold"
                    style={{ fontSize: "18px", color: "#271078" }}
                  >
                    {" "}
                    Total Score:
                  </span>
                  <span
                    style={{ fontSize: "18px", color: "#271078" }}
                    className=" font-bold"
                  >
                    {data?.collection_strategy_total_points ?? "-"}
                  </span>
                </span>
              </div> */}

              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "0.5rem",
                  border: "1px solid #DFDFDF",
                }}
                className="px-8 py-12"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    // marginBottom: "2rem",
                  }}
                >
                  <div
                    className="flex items-center gap-2"
                    style={{ marginLeft: "-5px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="rgb(39, 16, 120)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="2" width="6" height="4" rx="1" />
                      <path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
                      <path d="m9 14 2 2 4-4" />
                    </svg>

                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#271078",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Collection Strategy Parameters
                    </h4>
                  </div>
                  {!isEditMode &&
                    storage.getItem("role") !== "Agent" &&
                    storage.getItem("role") !== "Agenth" && (
                      <div
                        style={{
                          // marginTop: "2.5rem",
                          display: "flex",
                          justifyContent: "flex-end",
                          paddingRight: "1rem",
                        }}
                      >
                        <button
                          onClick={() => {
                            setIsEditMode(true);
                            setShowAllInfo(true);
                          }}
                          style={{
                            backgroundColor: "#fff",
                            color: "#271078",
                            padding: "0.8rem 2.5rem",
                            borderRadius: "10px",
                            fontWeight: "800",
                            border: "2px solid #271078",
                            cursor: "pointer",
                            fontSize: "15px",
                            transition: "all 0.2s",
                            boxShadow: "0 4px 6px -1px rgba(39, 16, 120, 0.1)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#271078";
                            e.currentTarget.style.color = "white";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "white";
                            e.currentTarget.style.color = "#271078";
                          }}
                        >
                          Edit Reports
                        </button>
                      </div>
                    )}
                </div>

                <div
                  style={{
                    marginTop: "2rem",
                    overflowX: "auto",
                    paddingBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      minWidth: "100%",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2rem",
                    }}
                  >
                    {allVersions.length === 1 ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "1.5rem",
                          width: "100%",
                          padding: "0 1rem",
                          borderBottom: "1px solid #f3f4f6",
                          paddingBottom: "1rem",
                        }}
                      >
                        <div style={{ display: "flex", gap: "2rem" }}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                fontWeight: "600",
                                textTransform: "uppercase",
                              }}
                            >
                              DataKlout Accuracy
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "800",
                                color: "#111827",
                              }}
                            >
                              {
                                allVersions[0].data
                                  ?.dataklout_collection_strategy_accuracy_percent
                              }
                              %
                            </span>
                          </div>
                          {!allVersions[0].isOriginal && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "2px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  fontWeight: "600",
                                  textTransform: "uppercase",
                                }}
                              >
                                Edited By
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  color: "#374151",
                                }}
                              >
                                {allVersions[0].editedBy}
                              </span>
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                fontWeight: "600",
                                textTransform: "uppercase",
                              }}
                            >
                              Date
                            </span>
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#374151",
                              }}
                            >
                              {allVersions[0].editedAt}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          width: "auto",
                          paddingRight: "1rem",
                        }}
                      >
                        <div
                          style={{
                            minWidth: "420px",
                            flex: "0 0 420px",
                            position: "sticky",
                            left: 0,
                            zIndex: 20,
                            backgroundColor: "white",
                          }}
                        />
                        {allVersions.map((v, i) => (
                          <div
                            key={i}
                            style={{
                              minWidth: 0,
                              flex: 1,
                              padding: "1rem",
                              borderRadius: "12px",
                              backgroundColor: "white",
                              color: "#1f2937",
                              border: "1px solid #e5e7eb",
                              boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "800",
                                fontSize: "16px",
                                marginBottom: "8px",
                                color: "#1f2937",
                              }}
                            >
                              {v.label}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                                marginBottom: "8px",
                                backgroundColor: "#f3f4f6",
                                padding: "6px",
                                borderRadius: "6px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "11px",
                                  textTransform: "uppercase",
                                  color: "#6b7280",
                                  fontWeight: "700",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {v.isOriginal
                                  ? "DataKlout Accuracy"
                                  : "Reviewer Accuracy"}{" "}
                                | Score
                              </span>

                              <span
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "800",
                                  color: "#111827",
                                }}
                              >
                                {v.isOriginal
                                  ? v.data
                                      ?.dataklout_collection_strategy_accuracy_percent
                                  : v.data?.reviewer_percentage}{" "}
                                % |{" "}
                                {v.isOriginal
                                  ? v.data?.collection_strategy_total_points
                                  : v.revised_points}{" "}
                                Points
                              </span>
                            </div>
                            {/* {console.log(v)} */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontSize: "13px",
                                color: "#1f2937",
                                marginTop: "8px",
                                paddingTop: "8px",
                                borderTop: "1px solid #f3f4f6",
                                fontWeight: "600",
                              }}
                            >
                              {" "}
                              {!v.isOriginal ? (
                                <>
                                  <span>
                                    By:{" "}
                                    <span style={{ color: "#2563eb" }}>
                                      {v.editedBy}
                                    </span>
                                  </span>
                                  <span style={{ fontSize: "12px" }}>
                                    {v.editedAt}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span>
                                    By:{" "}
                                    <span style={{ color: "#2563eb" }}>
                                      DataKlout
                                    </span>
                                  </span>
                                  <span style={{ fontSize: "12px" }}>
                                    {data?.call_date}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                        {allVersions.length > 1 && (
                          <div
                            style={{
                              minWidth: "250px",
                              flex: "0 0 250px",
                              padding: "1rem",
                              borderRadius: "12px",

                              color: "#1f2937",
                              border: "1px solid #e5e7eb",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "800",
                                fontSize: "16px",
                                color: "#271078",
                              }}
                            >
                              Revision Trail
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {parameters.map((category, idx) => {
                      const calculateCategoryScore = (cat, data) => {
                        if (!cat || !data) return 0;
                        let score = 0;
                        cat.items.forEach((item) => {
                          if (data[item.valueKey]) {
                            score += item.pointsAvailable;
                          }
                        });
                        return score;
                      };

                      return (
                        <div
                          key={idx}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            background: "white",
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                        >
                          <div
                            style={{
                              padding: "1rem 1.5rem",
                              background: "#f9fafb",
                              borderBottom: "1px solid #e5e7eb",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "1rem",
                            }}
                          >
                            <h3
                              style={{
                                margin: 0,
                                color: "#111827",
                                fontSize: "16px",
                                fontWeight: "800",
                                width:
                                  allVersions.length === 1 ? "60%" : "420px",
                                flex:
                                  allVersions.length === 1
                                    ? "0 0 60%"
                                    : "0 0 420px",
                              }}
                            >
                              {category.title}
                            </h3>

                            <div
                              style={{
                                display: "flex",
                                flex: 1,
                                gap: "1rem",
                                alignItems: "center",
                                paddingRight: "1rem",
                                position: "relative",
                              }}
                            >
                              {allVersions.map((v, vIdx) => {
                                const effectiveData = v.data;
                                return (
                                  <div
                                    key={vIdx}
                                    style={{
                                      flex: 1,
                                      display: "flex",
                                      justifyContent:
                                        allVersions.length === 1
                                          ? "flex-start"
                                          : "center",
                                      paddingLeft:
                                        allVersions.length === 1 ? "12px" : "0",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: "700",
                                        color: "#271078",
                                        fontSize: "14px",
                                        backgroundColor: "white",
                                        padding: "0.25rem 0.75rem",
                                        borderRadius: "6px",
                                        border: "1px solid #e5e7eb",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Points:{" "}
                                      {calculateCategoryScore(
                                        category,
                                        effectiveData,
                                      )}{" "}
                                      / {category.totalPoints}
                                    </span>
                                  </div>
                                );
                              })}
                              {allVersions.length > 1 && (
                                <div
                                  style={{
                                    width: "250px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "700",
                                      color: "#271078",
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                      padding: "0.25rem 0.75rem",
                                      borderRadius: "6px",
                                      border: "1px solid #e5e7eb",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Max Points: {category.totalPoints}
                                  </span>
                                </div>
                              )}
                              {allVersions.length === 1 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    right: "0",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "700",
                                      color: "#271078",
                                      fontSize: "14px",
                                      backgroundColor: "white",
                                      padding: "0.25rem 0.75rem",
                                      borderRadius: "6px",
                                      border: "1px solid #e5e7eb",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Max Points: {category.totalPoints}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {category.items.map((item, itemIdx) => (
                            <div
                              key={itemIdx}
                              style={{
                                display: "flex",
                                borderBottom:
                                  itemIdx === category.items.length - 1
                                    ? "none"
                                    : "1px solid #f3f4f6",
                              }}
                            >
                              <div
                                style={{
                                  width:
                                    allVersions.length === 1 ? "60%" : "420px",
                                  flex:
                                    allVersions.length === 1
                                      ? "0 0 60%"
                                      : "0 0 420px",
                                  padding: "1.25rem",
                                  background: "white",
                                  position:
                                    allVersions.length === 1
                                      ? "relative"
                                      : "sticky",
                                  left: 0,
                                  zIndex: 10,
                                  borderRight: "1px solid #f3f4f6",
                                  display: "flex",
                                  alignItems: "center",
                                  boxShadow:
                                    allVersions.length === 1
                                      ? "none"
                                      : "2px 0 5px -2px rgba(0,0,0,0.05)",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#374151",
                                    fontWeight: "600",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {item.label}
                                </span>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: "0.5rem",
                                  padding: "0.5rem",
                                  flex: 1,
                                  justifyContent: "flex-start",
                                }}
                              >
                                {allVersions.map((v, vIdx) => {
                                  const isCurrent =
                                    vIdx === allVersions.length - 1;
                                  const effectiveData =
                                    isEditMode && isCurrent ? data : v.data;

                                  const value = !!effectiveData[item.valueKey];

                                  const changed = isFieldChangedInVersion(
                                    item.valueKey,
                                    v,
                                  );
                                  const bg = changed
                                    ? v.color || "#eff6ff"
                                    : "transparent";

                                  return (
                                    <div
                                      key={vIdx}
                                      style={{
                                        minWidth: 0,
                                        flex: 1,
                                        padding: "14px 12px",
                                        borderRadius: "8px",
                                        backgroundColor: bg,
                                        border: changed
                                          ? `1px solid ${v.color?.replace(
                                              "0.8",
                                              "1",
                                            )}`
                                          : "1px solid transparent",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems:
                                          allVersions.length === 1
                                            ? "flex-start"
                                            : "center",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "1rem",
                                          alignItems: "center",
                                          width: "100%",
                                          justifyContent:
                                            allVersions.length === 1
                                              ? "flex-start"
                                              : "center",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontWeight: "700",
                                            color: "#374151",
                                            fontSize: "13px",
                                            minWidth: "40px",
                                            textAlign: "right",
                                          }}
                                        >
                                          {value ? item.pointsAvailable : 0} pts
                                        </span>
                                        <div
                                          style={{
                                            height: "20px",
                                            width: "1px",
                                            backgroundColor:
                                              allVersions.length === 1 &&
                                              !changed
                                                ? "transparent"
                                                : "#e5e7eb",
                                          }}
                                        ></div>
                                        <span
                                          style={{
                                            fontWeight: "800",
                                            color: value
                                              ? "#059669"
                                              : "#dc2626",
                                            backgroundColor: value
                                              ? "#ecfdf5"
                                              : "#fef2f2",
                                            padding: "4px 12px",
                                            borderRadius: "6px",
                                            fontSize: "13px",
                                            minWidth: "50px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {value ? "Yes" : "No"}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}

                                {allVersions.length > 1 && (
                                  <div
                                    style={{
                                      width: "250px",
                                      flex: "0 0 250px",
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "center",
                                      borderLeft: "1px solid #f3f4f6",
                                    }}
                                  >
                                    {getFieldTrail(item.valueKey).length > 0 ? (
                                      getFieldTrail(item.valueKey)
                                        .slice(-1)
                                        .map((t, tidx) => (
                                          <div
                                            key={tidx}
                                            style={{
                                              flex: 1,
                                              fontSize: "12px",
                                              padding: "14px 12px",
                                              borderRadius: "8px",
                                              backgroundColor:
                                                "hsla(35, 100%, 94%, 1)",
                                              color: "#92400e",
                                              display: "flex",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              gap: "1rem",
                                              fontWeight: "800",
                                              border:
                                                "1px solid hsla(35, 100%, 85%, 1)",
                                              width: "100%",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontWeight: "700",
                                                color: "#92400e",
                                                fontSize: "13px",
                                                minWidth: "40px",
                                                textAlign: "right",
                                              }}
                                            >
                                              {(t.to === "True" || t.to === true
                                                ? item.pointsAvailable
                                                : 0) + " pts"}
                                            </span>
                                            <div
                                              style={{
                                                height: "20px",
                                                width: "1px",
                                                backgroundColor:
                                                  "hsla(35, 100%, 85%, 1)",
                                              }}
                                            ></div>
                                            <span
                                              style={{
                                                fontWeight: "800",
                                                color:
                                                  t.to === "True" ||
                                                  t.to === true
                                                    ? "#059669"
                                                    : "#dc2626",
                                                backgroundColor:
                                                  t.to === "True" ||
                                                  t.to === true
                                                    ? "#ecfdf5"
                                                    : "#fef2f2",
                                                padding: "4px 12px",
                                                borderRadius: "6px",
                                                fontSize: "13px",
                                                minWidth: "50px",
                                                textAlign: "center",
                                                border:
                                                  "1px solid rgba(0,0,0,0.05)",
                                              }}
                                            >
                                              {t.to === "True" || t.to === true
                                                ? "Yes"
                                                : "No"}
                                            </span>
                                          </div>
                                        ))
                                    ) : (
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "1rem",
                                          alignItems: "center",
                                          width: "100%",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontWeight: "700",
                                            color: "#374151",
                                            fontSize: "13px",
                                            minWidth: "40px",
                                            textAlign: "right",
                                          }}
                                        >
                                          {(!!allVersions[0].data[item.valueKey]
                                            ? item.pointsAvailable
                                            : 0) + " pts"}
                                        </span>
                                        <div
                                          style={{
                                            height: "20px",
                                            width: "1px",
                                            backgroundColor: "#e5e7eb",
                                          }}
                                        ></div>
                                        <span
                                          style={{
                                            fontWeight: "800",
                                            color: !!allVersions[0].data[
                                              item.valueKey
                                            ]
                                              ? "#059669"
                                              : "#dc2626",
                                            backgroundColor: !!allVersions[0]
                                              .data[item.valueKey]
                                              ? "#ecfdf5"
                                              : "#fef2f2",
                                            padding: "4px 12px",
                                            borderRadius: "6px",
                                            fontSize: "13px",
                                            minWidth: "50px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {!!allVersions[0].data[item.valueKey]
                                            ? "Yes"
                                            : "No"}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  {showAllInfo && (
                    <div
                      style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2rem",
                        backgroundColor: "rgba(0,0,0,0.6)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "white",
                          width: "90%",
                          maxWidth: "1000px",
                          height: "90vh",
                          borderRadius: "1rem",
                          border: "1px solid #DFDFDF",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <div
                          style={{
                            padding: "1.5rem 2rem",
                            borderBottom: "1px solid #e5e7eb",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#f9fafb",
                          }}
                        >
                          <h2
                            style={{
                              fontSize: "1.5rem",
                              fontWeight: "700",
                              color: "#271078",
                              margin: 0,
                            }}
                          >
                            Edit Report
                          </h2>
                          <button
                            onClick={handleCloseModal}
                            style={{
                              padding: "0.5rem",
                              borderRadius: "0.5rem",
                              border: "1px solid #e5e7eb",
                              backgroundColor: "white",
                              cursor: "pointer",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                        <div
                          style={{
                            margin: "1.5rem 2rem 0",
                            padding: "12px 16px",
                            backgroundColor: "#eff6ff",
                            border: "1px solid #bfdbfe",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                          </svg>
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#1e40af",
                              fontWeight: "700",
                            }}
                          >
                            You are currently in Editing Mode. Any changes made
                            will be saved as a new version of the report.
                          </span>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            overflowY: "auto",
                            padding: "2rem",
                          }}
                        >
                          {parameters.map((param, idx) => (
                            <div
                              key={idx}
                              style={{
                                marginBottom: "2rem",
                                backgroundColor: "#fcfcfc",
                                padding: "1.5rem",
                                borderRadius: "0.5rem",
                                border: "1px solid #DFDFDF",
                              }}
                            >
                              <h3
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color: "#271078",
                                  marginBottom: "1rem",
                                }}
                              >
                                {param.title}
                              </h3>
                              {param.items.map((item, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "1rem",
                                    paddingBottom: "1rem",
                                    borderBottom: "1px solid #f0f0f0",
                                  }}
                                >
                                  <label
                                    style={{
                                      fontSize: "14px",
                                      color: "#374151",
                                      fontWeight: "600",
                                      flex: 1,
                                    }}
                                  >
                                    {item.label}
                                  </label>
                                  <div style={{ display: "flex", gap: "1rem" }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                      }}
                                    >
                                      <select
                                        value={
                                          data[item.valueKey] ? "true" : "false"
                                        }
                                        onChange={(e) =>
                                          handleFieldUpdate(
                                            item.valueKey,
                                            e.target.value === "true",
                                          )
                                        }
                                        style={{
                                          padding: "0.4rem 2rem 0.4rem 0.6rem", // Extra right padding for arrow
                                          borderRadius: "0.5rem",
                                          border: "1px solid #d1d5db",
                                          backgroundColor: "white",
                                          color: "#374151",
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          cursor: "pointer",
                                          appearance: "none", // Remove default arrow
                                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                                          backgroundPosition:
                                            "right 0.5rem center",
                                          backgroundRepeat: "no-repeat",
                                          backgroundSize: "1.5em 1.5em",
                                          minWidth: "80px",
                                        }}
                                      >
                                        <option value="true">Yes</option>
                                        <option value="false">No</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            padding: "1.5rem 2rem",
                            borderTop: "1px solid #e5e7eb",
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "1rem",
                            backgroundColor: "#f9fafb",
                          }}
                        >
                          <button
                            onClick={handleCloseModal}
                            style={{
                              padding: "0.75rem 1.5rem",
                              borderRadius: "0.5rem",
                              border: "1px solid #d1d5db",
                              backgroundColor: "white",
                              fontWeight: "600",
                              cursor: "pointer",
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveAttempt}
                            style={{
                              padding: "0.75rem 1.5rem",
                              borderRadius: "0.5rem",
                              backgroundColor: "#271078",
                              color: "white",
                              fontWeight: "600",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            Review Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Removed old floating toolbar for inline edit */}
                  {isEditMode && (
                    <div
                      style={{
                        position: "sticky",
                        bottom: "20px",
                        margin: "2rem auto 0",
                        display: "flex",
                        justifyContent: "center",
                        gap: "1rem",
                        zIndex: 100,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        padding: "1rem",
                        backdropFilter: "blur(8px)",
                        borderRadius: "16px",
                        boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
                        width: "fit-content",
                        border: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      <button
                        onClick={() => {
                          setIsEditMode(false);
                          fetchDetail();
                        }}
                        style={{
                          padding: "0.8rem 2.5rem",
                          borderRadius: "10px",
                          fontWeight: "800",
                          border: "1px solid #d1d5db",
                          cursor: "pointer",
                          background: "white",
                          color: "#374151",
                          fontSize: "15px",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveAttempt}
                        style={{
                          backgroundColor: "#271078",
                          color: "white",
                          padding: "0.8rem 2.5rem",
                          borderRadius: "10px",
                          fontWeight: "800",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "15px",
                          boxShadow: "0 4px 12px rgba(39, 16, 120, 0.3)",
                        }}
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
                {showConfirmSave && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 10000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        backdropFilter: "blur(12px)",
                      }}
                      onClick={() => setShowConfirmSave(false)}
                    ></div>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "480px",
                        backgroundColor: "white",
                        borderRadius: "16px",
                        position: "relative",
                        zIndex: 1,
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "1.5rem 1.75rem",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "1.25rem",
                            margin: 0,
                            fontWeight: "800",
                            color: "#111827",
                            letterSpacing: "-0.025em",
                          }}
                        >
                          Review Save Request
                        </h3>
                        <p
                          style={{
                            margin: "0.5rem 0 0",
                            color: "#6b7280",
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Confirm the following modifications to continue.
                        </p>
                      </div>

                      <div style={{ padding: "1.5rem 1.75rem" }}>
                        <div
                          style={{
                            maxHeight: "250px",
                            overflowY: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.75rem",
                            paddingRight: "4px",
                          }}
                        >
                          {changedFieldsList.map((change, idx) => (
                            <div
                              key={idx}
                              style={{
                                backgroundColor: "#f9f9fb",
                                padding: "1rem",
                                borderRadius: "12px",
                                border: "1px solid #eee",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "700",
                                  color: "#1f2937",
                                  marginBottom: "0.75rem",
                                  lineHeight: "1.4",
                                }}
                              >
                                {change.label}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "0.1rem 0",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  {/* <span
                                    style={{
                                      fontSize: "11px",
                                      color: "#9ca3af",
                                      fontWeight: "600",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    From
                                  </span> */}
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      padding: "0.25rem 0.6rem",
                                      borderRadius: "6px",
                                      backgroundColor:
                                        change.from === "Yes"
                                          ? "#d1fae5"
                                          : "#fee2e2",
                                      color:
                                        change.from === "Yes"
                                          ? "#065f46"
                                          : "#991b1b",
                                      fontWeight: "800",
                                    }}
                                  >
                                    {change.from}
                                  </span>
                                </div>

                                <span
                                  style={{
                                    color: "#d1d5db",
                                    fontSize: "18px",
                                    fontWeight: "300",
                                  }}
                                >
                                  &rarr;
                                </span>

                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  {/* <span
                                    style={{
                                      fontSize: "11px",
                                      color: "#9ca3af",
                                      fontWeight: "600",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    To
                                  </span> */}
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      padding: "0.25rem 0.6rem",
                                      borderRadius: "6px",
                                      backgroundColor:
                                        change.to === "Yes"
                                          ? "#d1fae5"
                                          : "#fee2e2",
                                      color:
                                        change.to === "Yes"
                                          ? "#065f46"
                                          : "#991b1b",
                                      fontWeight: "800",
                                      border: "1px solid currentColor",
                                    }}
                                  >
                                    {change.to}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "1.25rem 1.75rem",
                          backgroundColor: "#fff",
                          borderTop: "1px solid #f0f0f0",
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "1rem",
                        }}
                      >
                        <button
                          style={{
                            padding: "0.75rem 1.75rem",
                            fontSize: "15px",
                            fontWeight: "700",
                            borderRadius: "10px",
                            border: "1px solid #e5e7eb",
                            backgroundColor: "white",
                            color: "#4b5563",
                            cursor: "pointer",
                          }}
                          onClick={() => setShowConfirmSave(false)}
                        >
                          Cancel
                        </button>
                        <button
                          style={{
                            padding: "0.75rem 1.75rem",
                            fontSize: "15px",
                            fontWeight: "700",
                            borderRadius: "10px",
                            border: "none",
                            backgroundColor: "#271078",
                            color: "white",
                            cursor: "pointer",
                            boxShadow: "0 4px 6px -1px rgba(39, 16, 120, 0.2)",
                          }}
                          onClick={executeSave}
                        >
                          Confirm & Save
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Inline Comments Section */}
              <div
                id="comments-section"
                style={{
                  backgroundColor: "white",
                  borderRadius: "0.5rem",
                  border: "1px solid #DFDFDF",
                }}
                className="px-8 py-12"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "2rem",
                  }}
                >
                  <div
                    className="flex items-center gap-2"
                    style={{ marginLeft: "-5px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#271078"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>

                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#271078",
                        marginBottom: "0.25rem",
                        marginTop: "0.25rem",
                      }}
                    >
                      Comments
                    </h4>
                    {(notesData?.notes?.length ?? 0) > 0 && (
                      <span
                        style={{
                          backgroundColor: "#271078",
                          color: "white",
                          borderRadius: "12px",
                          padding: "2px 8px",
                          fontSize: "11px",
                          fontWeight: "700",
                          marginLeft: "8px",
                        }}
                      >
                        {notesData.notes.length}
                      </span>
                    )}
                  </div>
                  <div>
                    <button
                      className="cs-notes-add-btn"
                      onClick={() => {
                        setNotesInput("");
                        setShowNotesModal(true);
                      }}
                      style={{ padding: "8px 16px" }}
                    >
                      + Add Comment
                    </button>
                  </div>
                </div>

                <div style={{ flex: 1 }}>
                  {(notesData?.notes?.length ?? 0) === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "40px 0",
                        color: "#9ca3af",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#DFDFDF"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginBottom: "12px" }}
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                      <p style={{ fontSize: "14px", textAlign: "center" }}>
                        No comments added yet.
                        <br />
                        Click &ldquo;+ Add Comment&rdquo; to get started.
                      </p>
                    </div>
                  ) : (
                    notesData.notes.map((note) => (
                      <div
                        key={note.id}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          padding: "16px",
                          marginBottom: "12px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          border: "1px solid #DFDFDF",
                          boxShadow: "0 1px 4px rgba(39,16,120,0.06)",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "15px",
                              color: "#171717",
                              lineHeight: "1.6",
                              wordBreak: "break-word",
                            }}
                          >
                            {note.text}
                          </p>
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#9ca3af",
                              marginTop: "8px",
                              display: "block",
                              fontWeight: "500",
                            }}
                          >
                            {note.date}
                          </span>
                        </div>
                        <button
                          onClick={() => setNoteToDelete(note.id)}
                          title="Delete note"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#c2c2c2",
                            marginLeft: "16px",
                            padding: "4px",
                            flexShrink: 0,
                            transition: "color 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#ef4444")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#c2c2c2")
                          }
                        >
                          <i
                            className="fa fa-trash"
                            aria-hidden="true"
                            style={{ fontSize: "16px" }}
                          />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />

      <style>{`
        .cs-notes-add-btn {
          background-color: #fff;
          color: #271078;
          border: 2px solid #271078;
          border-radius: 6px;
          padding: 5px 14px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          transition: background-color 0.2s, color 0.2s;
        }
        .cs-notes-add-btn:hover {
          background-color: #271078;
          color: white;
        }
        .cs-notes-save-btn {
          background-color: #fff;
          color: #271078;
          border: 2px solid #271078;
          border-radius: 8px;
          padding: 7px 18px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: background-color 0.2s, color 0.2s;
        }
        .cs-notes-save-btn:hover {
          background-color: #271078;
          color: white;
        }
      `}</style>

      {/* NOTES MODAL */}
      {showNotesModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9990,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => {
            setShowNotesModal(false);
          }}
        >
          <div
            style={{
              background: "#FDFCFF",
              borderRadius: "12px",
              width: "520px",
              maxWidth: "94vw",
              maxHeight: "80vh",
              boxShadow: "0 12px 40px rgba(39,16,120,0.18)",
              display: "flex",
              flexDirection: "column",
              border: "1px solid #DFDFDF",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 24px",
                borderBottom: "1px solid #DFDFDF",
                background: "white",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#271078",
                  }}
                >
                  Add Comment
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <button
                  onClick={() => {
                    setShowNotesModal(false);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "22px",
                    cursor: "pointer",
                    color: "#6b7280",
                    lineHeight: 1,
                  }}
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Body: add-note form */}
            <div style={{ padding: "20px 24px" }}>
              <p
                style={{
                  fontSize: "13px",
                  color: "#484848",
                  marginBottom: "10px",
                  fontWeight: "600",
                }}
              >
                Write your comments
              </p>
              <textarea
                rows={5}
                placeholder="Write your comments here..."
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #C2C2C2",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "14px",
                  color: "#171717",
                  resize: "vertical",
                  outline: "none",
                  boxSizing: "border-box",
                  background: "white",
                  fontFamily: "inherit",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "14px",
                }}
              >
                <button
                  onClick={() => setShowNotesModal(false)}
                  style={{
                    backgroundColor: "white",
                    color: "#484848",
                    border: "1px solid #C2C2C2",
                    borderRadius: "8px",
                    padding: "7px 18px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button className="cs-notes-save-btn" onClick={handleAddNote}>
                  Save Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE NOTE CONFIRMATION MODAL */}
      {noteToDelete !== null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setNoteToDelete(null)}
        >
          <div
            style={{
              background: "#FDFCFF",
              borderRadius: "12px",
              padding: "32px",
              width: "380px",
              maxWidth: "90vw",
              boxShadow: "0 12px 40px rgba(39,16,120,0.18)",
              border: "1px solid #DFDFDF",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <i
              className="fa fa-trash"
              style={{
                fontSize: "32px",
                color: "#ef4444",
                marginBottom: "14px",
                display: "block",
              }}
            />
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#271078",
                marginBottom: "8px",
              }}
            >
              Delete Note?
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "22px",
              }}
            >
              Are you sure you want to delete this note? This action cannot be
              undone.
            </p>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "12px" }}
            >
              <button
                onClick={() => setNoteToDelete(null)}
                style={{
                  backgroundColor: "white",
                  color: "#484848",
                  border: "1px solid #C2C2C2",
                  borderRadius: "8px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteNote}
                style={{
                  backgroundColor: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Detail;
